import React, { useEffect, useRef, useMemo } from "react";
import * as Chart from "chart.js";
import moment from "moment-hijri";
import HeadingComponent from "../shared/heading";

const { Chart: ChartJS, registerables } = Chart;
ChartJS.register(...registerables);

function groupDataByYear(data) {
  const grouped = {};
  data.forEach((item) => {
    if (!grouped[item.year]) grouped[item.year] = [];
    grouped[item.year].push(item);
  });
  return grouped;
}

const NewChartKPI = ({
  chartData = [],
  title = "",
  subtitle = "",
  chartTitle = "",
  colors: colorsProp = {},
  labels: labelsProp = {},
  showDataLabels = true,
  showRamadanRow = false,
  ramadanBorderColor = "#ffd700",
  yAxisFormatters = {},
  yAxisLabel = "On Street Parking",
  bottomLabel = "Months",
}) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const finalLabelRects = useRef([]);
  const containerRef = useRef(null);
  const tableRef = useRef(null);
  const labelColRef = useRef(null);


  const colors = {
    revenue: colorsProp?.revenue ?? "#4C96D9",
    parkingBays: colorsProp?.parkingBays ?? colorsProp?.atv ?? "#C00000",
    transaction: colorsProp?.transaction ?? "#C05A1F",
    occupancy: colorsProp?.occupancy ?? colorsProp?.als ?? "#D49B00",
  };

  const dataLabels = {
    revenue: labelsProp?.revenue ?? "Monthly Avg Daily Revenue",
    parkingBays: labelsProp?.parkingBays ?? labelsProp?.transaction ?? "Total Parking Bays",
    transaction: labelsProp?.atv ?? "Monthly Avg Daily Transaction",
    occupancy: labelsProp?.occupancy ?? labelsProp?.als ?? "Parking Occupancy",
  };

  const leftFormatter = yAxisFormatters?.left ?? ((v) => v.toLocaleString());
  const rightFormatter = yAxisFormatters?.right ?? ((v) => `${v}%`);

  const themeColors = {
    textColor: "#e5e7eb",
    gridColor: "rgba(255, 255, 255, 0.1)",
    borderColor: "#4b5563",
    backgroundColor: "#6B6A6C",
    labelBoxBg: "rgba(0, 0, 0, 0.4)",
    labelTextColor: "#ffffff",
    tooltipBg: "rgba(31, 41, 55, 0.95)",
    tooltipBorder: "#4b5563",
  };

  const axisRanges = useMemo(() => {
    if (!chartData.length) {
      return { y: { min: 0, max: 100 }, y1: { min: -5, max: 50 }, y2: { min: 0, max: 100 } };
    }

    const pad = (vals, factor, topFactor = factor) => {
      const filtered = vals.filter((v) => v != null && !isNaN(v));
      if (!filtered.length) return { min: 0, max: 100 };
      const mn = Math.min(...filtered);
      const mx = Math.max(...filtered);
      const range = mx - mn || Math.abs(mx) * 0.2 || 1;
      return {
        min: Math.max(0, Math.floor(mn - range * factor)),
        max: Math.ceil(mx + range * topFactor),
      };
    };

    // Revenue axis gets extra headroom at the top so stacked value labels
    // (and the Ramadhan callout box, when shown) have real vertical room to
    // sit in near the bar instead of being pushed far away.
    const yRange = pad(
      [
        ...chartData.map((d) => d.revenue),
      ],
      0.25,
      showRamadanRow ? 0.55 : 0.35
    );

    // Separate range for the small-value lines (transaction & parkingBays)
    // so they don't get crushed near the bottom of the shared revenue axis.
    const y2Range = pad(
      [
        ...chartData.map((d) => d.transaction),
        ...chartData.map((d) => d.parkingBays),
      ],
      0.6
    );

    const occVals = chartData.map((d) => d.occupancy).filter((v) => v != null);
    const occMax = occVals.length ? Math.max(...occVals) : 50;
    const y1Max = Math.ceil(occMax / 0.3);
    const y1Range = y1Max - 0;
    const y1Min = -Math.ceil(y1Range * 0.1);

    return { y: yRange, y1: { min: y1Min, max: y1Max }, y2: y2Range };
  }, [chartData, showRamadanRow]);

  const getRamadanMonths = useMemo(() => {
    if (!showRamadanRow || !chartData.length) return [];

    const monthMap = {
      Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
      Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
    };

    const ramadanData = [];
    chartData.forEach((item, idx) => {
      const monthName = item.month.split("-")[0];
      const year = parseInt(item.year);
      const monthNum = monthMap[monthName] !== undefined ? monthMap[monthName] : 0;

      const hijriYear = year - 579;
      const ramadanFirstDay = moment(`${hijriYear}-9-1`, "iYYYY-iM-iD");
      const ramadanStartGregMonth = ramadanFirstDay.month();
      const ramadanStartGregYear = ramadanFirstDay.year();
      const isRamadan = ramadanStartGregYear === year && ramadanStartGregMonth === monthNum;

      if (isRamadan) ramadanData.push(idx);
    });

    return ramadanData;
  }, [chartData, showRamadanRow]);

  const groupedData = groupDataByYear(chartData);
  const months = chartData.map((d) => d.month.split("-")[0]);
  const yearRanges = Object.keys(groupedData).map((year) => ({
    year,
    span: groupedData[year].length,
  }));

  useEffect(() => {
    if (!chartRef.current || !chartData.length) return;
    if (chartInstance.current) chartInstance.current.destroy();

    const ctx = chartRef.current.getContext("2d");
    finalLabelRects.current = [];
    const ramadanMonthIndices = new Set(getRamadanMonths);

    // ✅ Value labels get computed first, but NOT drawn immediately —
    // ramadanPlugin gets first pick of its natural spot, nudges any clashing
    // value label out of the way, and only then does drawValueLabelsPlugin
    // draw everything, followed by drawRamadanBoxesPlugin drawing Ramadan
    // LAST so it always sits fully visible on top.
    let pendingLabels = [];
    let pendingRamadanBoxes = [];

    // ─── HELPERS ────────────────────────────────────────────────────────────
    const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
    // ────────────────────────────────────────────────────────────────────────

    // Custom legend plugin
    const customLegendPlugin = {
      id: "customLegend",
      afterDraw(chart) {
        const legend = chart.legend;
        if (!legend?.legendItems) return;
        const ctx = chart.ctx;
        ctx.save();
        legend.legendItems.forEach((item, i) => {
          const hitBox = legend.legendHitBoxes?.[i];
          if (!hitBox) return;
          const centerY = hitBox.top + hitBox.height / 2;
          const iconW = 22;
          const iconX = hitBox.left;
          ctx.clearRect(iconX, hitBox.top, iconW + 2, hitBox.height);

          if (i === 0) {
            ctx.fillStyle = colors.revenue;
            ctx.fillRect(iconX, centerY - 5, iconW, 10);
          } else if (i === 1) {
            ctx.strokeStyle = colors.parkingBays;
            ctx.lineWidth = 2;
            ctx.setLineDash([5, 4]);
            ctx.beginPath();
            ctx.moveTo(iconX, centerY);
            ctx.lineTo(iconX + iconW, centerY);
            ctx.stroke();
            ctx.setLineDash([]);
          } else if (i === 2) {
            ctx.strokeStyle = colors.transaction;
            ctx.lineWidth = 2.5;
            ctx.setLineDash([]);
            ctx.beginPath();
            ctx.moveTo(iconX, centerY);
            ctx.lineTo(iconX + iconW, centerY);
            ctx.stroke();
            ctx.fillStyle = colors.transaction;
            ctx.beginPath();
            ctx.arc(iconX + iconW / 2, centerY, 4, 0, Math.PI * 2);
            ctx.fill();
          } else if (i === 3) {
            ctx.strokeStyle = colors.occupancy;
            ctx.lineWidth = 2.5;
            ctx.setLineDash([]);
            ctx.beginPath();
            ctx.moveTo(iconX, centerY);
            ctx.lineTo(iconX + iconW, centerY);
            ctx.stroke();
          }
        });
        ctx.restore();
      },
    };

    // Shadow plugin
    const shadowPlugin = {
      id: "shadowPlugin",
      beforeDatasetDraw(chart, args) {
  const { ctx } = chart;
  const dsIndex = args.index;

  ctx.save();

  if (dsIndex === 1) {
    ctx.shadowColor = colors.parkingBays + "90";
    ctx.shadowBlur = 12;
    ctx.shadowOffsetY = 3;
  }
},

afterDatasetDraw(chart) {
  const { ctx } = chart;
  ctx.restore();
}
    };

    // ─── UNIFIED LABEL PLUGIN (single-pool overlap resolution, BarChartWithTable style) ───
    const unifiedLabelPlugin = {
      id: "unifiedLabels",
      afterDatasetsDraw(chart) {
        if (!showDataLabels) return;

        finalLabelRects.current = []; // reset before re-drawing

        const ctx = chart.ctx;
        const yScale = chart.scales.y;
        const chartTop = yScale.top + 4;
        const chartBottom = yScale.bottom - 4;
        const PAD = 2;

        ctx.save();

        const allLabels = [];

        // ── 1. REVENUE labels (above bar tops) ──────────────────────────────
        const revMeta = chart.getDatasetMeta(0);
        if (revMeta && !revMeta.hidden) {
          ctx.font = "bold 10px Arial";
          revMeta.data.forEach((bar, i) => {
            const value = chart.data.datasets[0].data[i];
            if (value == null || value === 0) return;
            if (ramadanMonthIndices.has(i)) return;
            const text = leftFormatter(value);
            const tw = ctx.measureText(text).width;
            const w = tw + PAD * 2 + 4, h = 17;
            allLabels.push({
              xPos: bar.x,
              yPos: clamp(bar.y - h - 8, chartTop, chartBottom),
              barX: bar.x,
              barY: bar.y,
              text, bw: w, bh: h,
              color: colors.revenue,
              lineWidth: 1.2,
              zOrder: 3,
              font: "bold 10px Arial",
            });
          });
        }

        // ── 2. TRANSACTION labels (above line points) ────────────────────────
        const txMeta = chart.getDatasetMeta(2);
        if (txMeta && !txMeta.hidden) {
          ctx.font = "bold 8px Arial";
          txMeta.data.forEach((pt, i) => {
            const value = chart.data.datasets[2].data[i];
            if (value == null || value === 0) return;
            const text = Math.round(value).toLocaleString();
            const tw = ctx.measureText(text).width;
            const w = tw + PAD * 2 + 6, h = 15;
            allLabels.push({
              xPos: pt.x,
              yPos: clamp(pt.y - h - 10, chartTop, chartBottom),
              barX: pt.x,
              barY: pt.y,
              text, bw: w, bh: h,
              color: colors.transaction,
              lineWidth: 1,
              zOrder: 2,
              font: "bold 8px Arial",
            });
          });
        }

        // ── 3. PARKING BAYS labels (below dashed line) ───────────────────────
        const bayMeta = chart.getDatasetMeta(1);
        if (bayMeta && !bayMeta.hidden) {
          ctx.font = "bold 8px Arial";
          bayMeta.data.forEach((pt, i) => {
            const value = chart.data.datasets[1].data[i];
            if (value == null || value === 0) return;
            const text = Math.round(value).toLocaleString();
            const tw = ctx.measureText(text).width;
            const w = tw + PAD * 2 + 6, h = 15;
            allLabels.push({
              xPos: pt.x,
              yPos: clamp(pt.y + h + 10, chartTop, chartBottom),
              barX: pt.x,
              barY: pt.y,
              text, bw: w, bh: h,
              color: colors.parkingBays,
              lineWidth: 1,
              zOrder: 1,
              font: "bold 8px Arial",
            });
          });
        }

        // ── 4. OCCUPANCY labels (below occupancy line) ───────────────────────
        const occMeta = chart.getDatasetMeta(3);
        if (occMeta && !occMeta.hidden) {
          ctx.font = "bold 8px Arial";
          occMeta.data.forEach((pt, i) => {
            const value = chart.data.datasets[3].data[i];
            if (value == null || value === 0) return;
            const text = rightFormatter(value);
            const tw = ctx.measureText(text).width;
            const w = tw + PAD * 2 + 6, h = 15;
            allLabels.push({
              xPos: pt.x,
              yPos: clamp(pt.y + h + 10, chartTop, chartBottom),
              barX: pt.x,
              barY: pt.y,
              text, bw: w, bh: h,
              color: colors.occupancy,
              textColor: colors.occupancy,
              lineWidth: 1,
              zOrder: 0,
              font: "bold 8px Arial",
            });
          });
        }

        // ── Robust single-pool overlap resolution (ChartTableComponent style) ──
        const V_GAP = 4;
        const H_GAP = 3;
        const MAX_PASSES = 60;

        const rectsOverlap = (a, b) => {
          return !(
            (a.xPos + a.bw / 2 + H_GAP) < (b.xPos - b.bw / 2) ||
            (a.xPos - a.bw / 2 - H_GAP) > (b.xPos + b.bw / 2) ||
            (a.yPos + a.bh / 2 + V_GAP) < (b.yPos - b.bh / 2) ||
            (a.yPos - a.bh / 2 - V_GAP) > (b.yPos + b.bh / 2)
          );
        };

        for (let pass = 0; pass < MAX_PASSES; pass++) {
          let anyMoved = false;

          for (let i = 0; i < allLabels.length; i++) {
            for (let j = i + 1; j < allLabels.length; j++) {
              const a = allLabels[i];
              const b = allLabels[j];

              if (!rectsOverlap(a, b)) continue;

              anyMoved = true;

              // ✅ Whoever is physically higher (smaller y) gets pushed
              // further up; whoever is lower gets pushed further down —
              // regardless of which is "a" or "b". (Using a fixed a-above-b
              // formula here used to occasionally push two labels toward
              // each other instead of apart.)
              const hi = a.yPos <= b.yPos ? a : b;
              const lo = a.yPos <= b.yPos ? b : a;
              const overlapAmt = (hi.yPos + hi.bh / 2 + V_GAP) - (lo.yPos - lo.bh / 2);
              if (overlapAmt <= 0) continue;

              hi.yPos = clamp(hi.yPos - overlapAmt / 2, chartTop, chartBottom);
              lo.yPos = clamp(lo.yPos + overlapAmt / 2, chartTop, chartBottom);
            }
          }

          if (!anyMoved) break;
        }

        // ── Store all labels for later draw (lower zOrder first, so revenue
        // sits on top) — drawValueLabelsPlugin draws them AFTER ramadanPlugin
        // has had a chance to nudge any that clash with a Ramadhan callout ──
        pendingLabels.length = 0;
        allLabels
          .sort((a, b) => a.zOrder - b.zOrder)
          .forEach((l) => pendingLabels.push(l));

        ctx.restore();
      },
    };

    // ✅ DRAW VALUE LABELS PLUGIN — draws the value-label boxes computed by
    // unifiedLabelPlugin, AFTER ramadanPlugin has nudged any that clash with
    // a Ramadhan callout out of the way.
    const drawValueLabelsPlugin = {
      id: "drawValueLabels",
      afterDatasetsDraw(chart) {
        if (!showDataLabels) return;
        const ctx = chart.ctx;
        ctx.save();

        finalLabelRects.current = [];
        pendingLabels.forEach((l) => {
          const bx = l.xPos - l.bw / 2;
          const by = l.yPos - l.bh / 2;

          ctx.strokeStyle = l.color;
          ctx.lineWidth = l.lineWidth || 1.2;
          ctx.strokeRect(bx, by, l.bw, l.bh);

          ctx.fillStyle = l.textColor || "#ffffff";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.font = l.font || "bold 10px Arial";
          ctx.fillText(l.text, l.xPos, l.yPos);

          finalLabelRects.current.push({
            left: bx - 4, right: bx + l.bw + 4,
            top: by - 4, bottom: by + l.bh + 4,
          });
        });

        ctx.restore();
      },
    };

    // Ramadan plugin (unchanged)
    const ramadanPlugin = (() => {
      const hasRamadan = showRamadanRow && getRamadanMonths.length > 0;
      if (!hasRamadan) return { id: "ramadanPlugin_disabled", afterDraw() {} };

      return {
        id: "ramadanPlugin",
        afterDatasetsDraw(chart) {
          const ctx = chart.ctx;
          const xAxis = chart.scales.x;
          const yAxis = chart.scales.y;

          pendingRamadanBoxes.length = 0;

          const BOX_H = 24;
          const ARROW_W = 16;
          const H_PAD = 8;
          const V_PAD = 8;
          const BR = 5;
          const LABEL_FONT = "bold 9.5px Arial";
          const TEXT_PAD = 16; // inner left+right padding around the text

          const chartTop = yAxis.top;
          const chartBottom = yAxis.bottom;
          const chartLeft = xAxis.left;
          const chartRight = xAxis.right;

          const clampX = (x, anchorX) => {
            // Box is normally centered on the bar (anchorX - BOX_W / 2).
            // Allow it to drift left/right of that centered position so it
            // can dodge overlapping value labels, but cap the drift so the
            // connector arrow doesn't stretch into a long diagonal line.
            const maxShift = Math.min(BOX_W * 0.8, 95);
            const centered = anchorX - BOX_W / 2;
            const lo = centered - maxShift;
            const hi = centered + maxShift;
            const anchored = Math.max(lo, Math.min(hi, x));
            return Math.max(
              chartLeft + 4,
              Math.min(chartRight - BOX_W - 4, anchored)
            );
          };
          const clampY = (y) =>
            Math.max(chartTop + 4, Math.min(chartBottom - BOX_H - 4, y));

          const mkRect = (l, t, w, h) => ({
            left: l, right: l + w, top: t, bottom: t + h,
          });

          const overlaps = (a, b, hPad = H_PAD, vPad = V_PAD) =>
            a.left - hPad < b.right + hPad &&
            a.right + hPad > b.left - hPad &&
            a.top - vPad < b.bottom + vPad &&
            a.bottom + vPad > b.top - vPad;

          // ✅ Value labels are NOT obstacles here anymore — Ramadan claims
          // its natural spot, and any clashing value label gets nudged out
          // of the way afterwards (see the nudge step below).
          const obstacles = [];

          chart.data.datasets.forEach((dataset, dsIdx) => {
            if (dataset.type !== "bar") return;
            const meta = chart.getDatasetMeta(dsIdx);
            if (meta.hidden) return;
            meta.data.forEach((bar) => {
              if (!bar) return;
              try {
                const props = bar.getProps(["x", "y", "base", "width"], true);
                const bL = props.x - props.width / 2;
                const bR = props.x + props.width / 2;
                const bTop = Math.min(props.y, props.base);
                const bBot = Math.max(props.y, props.base);
                if (!isNaN(bL) && !isNaN(bTop)) {
                  obstacles.push({ left: bL - 3, right: bR + 3, top: bTop - 3, bottom: bBot + 3 });
                }
              } catch (_) {}
            });
          });

          chart.data.datasets.forEach((dataset, dsIdx) => {
            if (dataset.type === "bar") return;
            const meta = chart.getDatasetMeta(dsIdx);
            if (meta.hidden) return;
            meta.data.forEach((pt) => {
              if (!pt) return;
              const px = pt.x, py = pt.y;
              if (px == null || py == null || isNaN(px) || isNaN(py)) return;
              obstacles.push({ left: px - 4, right: px + 4, top: py - 4, bottom: py + 4 });
            });
          });

          const getBarTopY = (idx) => {
            const meta = chart.getDatasetMeta(0);
            if (meta?.data?.[idx]) {
              const bar = meta.data[idx];
              try {
                const props = bar.getProps(["y", "base"], true);
                return Math.min(props.y, props.base ?? props.y);
              } catch (_) {
                return Math.min(bar.y, bar.base ?? bar.y);
              }
            }
            return yAxis.getPixelForValue(0);
          };

          const items = [];
          let mi = 0;
          yearRanges.forEach((yr) => {
            for (let i = 0; i < yr.span; i++) {
              if (ramadanMonthIndices.has(mi)) {
                const val = chartData[mi]?.revenue ?? 0;
                const dataX = xAxis.getPixelForValue(mi);
                const dataY = getBarTopY(mi);
                items.push({
                  label: `RAMADHAN ${yr.year} ${months[mi]} : ${val.toLocaleString()}`,
                  dataX, dataY, revenue: val,
                });
              }
              mi++;
            }
          });

          if (!items.length) return;

          ctx.save();
          ctx.font = LABEL_FONT;
          const maxTextWidth = Math.max(
            ...items.map((it) => ctx.measureText(it.label).width)
          );
          ctx.restore();
          const chartWidth = xAxis.right - xAxis.left;
          const perBarWidth = chartWidth / (chartData.length || 1);
          const BOX_W = Math.max(
            Math.min(165, Math.max(110, perBarWidth * 3.2)),
            maxTextWidth + TEXT_PAD * 2
          );

          const vertGaps = [8, 20, 36, 55, 78, 105, 135, 170, 210, 255, 300, 350];
          const horizShifts = [0, -10, 10, -20, 20, -35, 35, -50, 50, -65, 65, -80, 80, -95, 95];
          const candidateOffsets = [];
          vertGaps.forEach((dy) =>
            horizShifts.forEach((dx) => candidateOffsets.push([dx, dy]))
          );

          const placedBoxes = [];

          items.forEach(({ label, dataX, dataY }) => {
            const arrowTipY = dataY;
            let chosenX = null, chosenY = null, placed = false;

            for (const [dx, dy] of candidateOffsets) {
              const candidateBoxX = clampX(dataX + dx - BOX_W / 2, dataX);
              const candidateBoxY = clampY(arrowTipY - dy - BOX_H);
              const testRect = mkRect(candidateBoxX, candidateBoxY, BOX_W, BOX_H);
              const allObs = [...obstacles, ...placedBoxes];
              if (!allObs.some((obs) => overlaps(testRect, obs))) {
                chosenX = candidateBoxX;
                chosenY = candidateBoxY;
                placed = true;
                break;
              }
            }

            if (!placed) {
              let bestScore = Infinity, bestX = clampX(dataX - BOX_W / 2, dataX), bestY = chartTop + 6;
              candidateOffsets.forEach(([dx, dy]) => {
                const bx = clampX(dataX + dx - BOX_W / 2, dataX);
                const by = clampY(arrowTipY - dy - BOX_H);
                const r = mkRect(bx, by, BOX_W, BOX_H);
                const allObs = [...obstacles, ...placedBoxes];
                const overlapCount = allObs.filter((obs) => overlaps(r, obs, 0, 0)).length;
                // Weight overlap heavily, but tie-break on distance from the
                // bar so the box doesn't drift far (and produce a long
                // connector line) when overlap counts are equal.
                const dist = Math.abs(dx) + Math.abs(dy) * 0.3;
                const score = overlapCount * 1000 + dist;
                if (score < bestScore) { bestScore = score; bestX = bx; bestY = by; }
              });
              chosenX = bestX;
              chosenY = bestY;
            }

            const chosenRect = mkRect(chosenX, chosenY, BOX_W, BOX_H);
            placedBoxes.push(chosenRect);
            obstacles.push({
              left: chosenRect.left - H_PAD, right: chosenRect.right + H_PAD,
              top: chosenRect.top - V_PAD, bottom: chosenRect.bottom + V_PAD,
            });

            const bTop = chosenY, bBot = chosenY + BOX_H;
            const baseCX = Math.min(
              Math.max(dataX, chosenX + ARROW_W / 2 + BR),
              chosenX + BOX_W - ARROW_W / 2 - BR
            );

            // ✅ Ramadan ne apni jagah le li — box aur uske arrow/tail dono
            // ke saath jo bhi value label clash kar rahi hai usay upar/neeche
            // (jis taraf usay zyada jagah mile) khiska do.
            {
              const ramadanBoxRect = { left: chosenX, right: chosenX + BOX_W, top: bTop, bottom: bBot };
              const ramadanTailRect = {
                left: Math.min(baseCX, dataX) - ARROW_W / 2 - 2,
                right: Math.max(baseCX, dataX) + ARROW_W / 2 + 2,
                top: bBot,
                bottom: arrowTipY,
              };
              const ramadanObstacles = [ramadanBoxRect, ramadanTailRect];
              const singleClash = (a, b) =>
                !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom);
              const clashesRamadan = (r) => ramadanObstacles.some((o) => singleClash(r, o));

              const minY = chartTop + 8;
              const maxY = chartBottom - 8;
              const pushStep = 6;

              const nudgeAwayFromRamadan = () => {
                pendingLabels.forEach((lbl) => {
                  let boxY = lbl.yPos - lbl.bh / 2;
                  let rect = { left: lbl.xPos - lbl.bw / 2 - 4, right: lbl.xPos + lbl.bw / 2 + 4, top: boxY - 2, bottom: boxY + lbl.bh + 4 };
                  if (!clashesRamadan(rect)) return;

                  const isAbove = lbl.yPos < lbl.barY;
                  let guard = 0;
                  while (clashesRamadan(rect) && guard < 80) {
                    let nextY = lbl.yPos + (isAbove ? -pushStep : pushStep);
                    nextY = Math.max(minY, Math.min(maxY, nextY));
                    if (nextY === lbl.yPos) break;
                    lbl.yPos = nextY;
                    boxY = lbl.yPos - lbl.bh / 2;
                    rect = { left: lbl.xPos - lbl.bw / 2 - 4, right: lbl.xPos + lbl.bw / 2 + 4, top: boxY - 2, bottom: boxY + lbl.bh + 4 };
                    guard++;
                  }

                  if (clashesRamadan(rect)) {
                    // Fallback: try the other direction from the original anchor
                    const orig = lbl.yPos;
                    let tryY = lbl.barY;
                    let g2 = 0;
                    let tryBoxY = tryY - lbl.bh / 2;
                    let tryRect = { left: lbl.xPos - lbl.bw / 2 - 4, right: lbl.xPos + lbl.bw / 2 + 4, top: tryBoxY - 2, bottom: tryBoxY + lbl.bh + 4 };
                    while (clashesRamadan(tryRect) && g2 < 80) {
                      let nextY = tryY + (isAbove ? pushStep : -pushStep);
                      nextY = Math.max(minY, Math.min(maxY, nextY));
                      if (nextY === tryY) break;
                      tryY = nextY;
                      tryBoxY = tryY - lbl.bh / 2;
                      tryRect = { left: lbl.xPos - lbl.bw / 2 - 4, right: lbl.xPos + lbl.bw / 2 + 4, top: tryBoxY - 2, bottom: tryBoxY + lbl.bh + 4 };
                      g2++;
                    }
                    lbl.yPos = !clashesRamadan(tryRect) ? tryY : orig;
                  }
                });
              };

              // ✅ Mutual-overlap pass among pendingLabels themselves.
              // FIX: overlapAmt used to assume "a" was always the higher one,
              // which produced a wrong-sign push (pulling two labels toward
              // each other instead of apart) whenever "b" actually sat above
              // "a" in pixel space. Now we always compute the higher/lower
              // pair explicitly before measuring the overlap.
              const resolveMutualOverlaps = () => {
                const V_GAP = 4, H_GAP = 3;
                const rectsOverlap = (a, b) =>
                  !(
                    (a.xPos + a.bw / 2 + H_GAP) < (b.xPos - b.bw / 2) ||
                    (a.xPos - a.bw / 2 - H_GAP) > (b.xPos + b.bw / 2) ||
                    (a.yPos + a.bh / 2 + V_GAP) < (b.yPos - b.bh / 2) ||
                    (a.yPos - a.bh / 2 - V_GAP) > (b.yPos + b.bh / 2)
                  );
                let movedAny = false;
                for (let pass = 0; pass < 40; pass++) {
                  let anyMoved = false;
                  for (let i = 0; i < pendingLabels.length; i++) {
                    for (let j = i + 1; j < pendingLabels.length; j++) {
                      const a = pendingLabels[i], b = pendingLabels[j];
                      if (!rectsOverlap(a, b)) continue;
                      anyMoved = true;
                      movedAny = true;

                      // ✅ Whoever is physically higher (smaller y) gets
                      // pushed further up; whoever is lower gets pushed
                      // further down — regardless of which is "a" or "b".
                      const hi = a.yPos <= b.yPos ? a : b;
                      const lo = a.yPos <= b.yPos ? b : a;
                      const overlapAmt = (hi.yPos + hi.bh / 2 + V_GAP) - (lo.yPos - lo.bh / 2);
                      if (overlapAmt <= 0) continue; // safety: no real overlap after all

                      hi.yPos = Math.max(minY, Math.min(maxY, hi.yPos - overlapAmt / 2));
                      lo.yPos = Math.max(minY, Math.min(maxY, lo.yPos + overlapAmt / 2));
                    }
                  }
                  if (!anyMoved) break;
                }
                return movedAny;
              };

              // ✅ Run both steps a few times, alternating — nudging away
              // from Ramadan can create a new mutual clash, and resolving a
              // mutual clash can push a label back toward Ramadan. A couple
              // of rounds lets both converge together.
              for (let round = 0; round < 3; round++) {
                nudgeAwayFromRamadan();
                const moved = resolveMutualOverlaps();
                if (!moved) break;
              }
            }

            pendingRamadanBoxes.push({ chosenX, chosenY, bTop, bBot, BOX_W, BR, ARROW_W, dataX, arrowTipY, baseCX, label });
          });
        },
      };
    })();

    // ✅ DRAW RAMADAN BOXES PLUGIN — draws Ramadan callouts LAST (after value
    // labels), same transparent-fill visual style as before, so they always
    // sit legibly on top without covering values that already moved away.
    const drawRamadanBoxesPlugin = {
      id: "drawRamadanBoxes",
      afterDatasetsDraw(chart) {
        const ctx = chart.ctx;
        const BOX_H = 24;
        const BR = 5;
        pendingRamadanBoxes.forEach(({ chosenX, chosenY, bTop, bBot, BOX_W, ARROW_W, dataX, arrowTipY, baseCX, label }) => {
          const baseBL = baseCX - ARROW_W / 2;
          const baseBR = baseCX + ARROW_W / 2;

          const drawShape = () => {
            ctx.beginPath();
            ctx.moveTo(chosenX + BR, bTop);
            ctx.lineTo(chosenX + BOX_W - BR, bTop);
            ctx.arcTo(chosenX + BOX_W, bTop, chosenX + BOX_W, bTop + BR, BR);
            ctx.lineTo(chosenX + BOX_W, bBot);
            ctx.lineTo(baseBR, bBot);
            ctx.lineTo(dataX, arrowTipY);
            ctx.lineTo(baseBL, bBot);
            ctx.lineTo(chosenX, bBot);
            ctx.lineTo(chosenX, bTop + BR);
            ctx.arcTo(chosenX, bTop, chosenX + BR, bTop, BR);
            ctx.closePath();
          };

          ctx.save();
          ctx.shadowColor = colors.transaction + "99";
          ctx.shadowBlur = 12;
          ctx.strokeStyle = colors.transaction;
          ctx.lineWidth = 0.5;
          drawShape();
          ctx.stroke();
          ctx.shadowColor = "transparent";
          ctx.shadowBlur = 0;
          ctx.fillStyle = "transparent";
          drawShape();
          ctx.fill();
          ctx.strokeStyle = ramadanBorderColor;
          ctx.lineWidth = 1.4;
          drawShape();
          ctx.stroke();
          ctx.fillStyle = "#ffffff";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.font = "bold 9.5px Arial";
          ctx.fillText(label, chosenX + BOX_W / 2, (bTop + bBot) / 2);
          ctx.restore();
        });
      },
    };

    // ─── alignTablePlugin ── keeps the table's month columns lined up under the
    // matching bar by measuring the chart's real plot-area pixels and sizing the
    // table's label column + overall width to match it exactly.
    const alignTablePlugin = {
      id: "alignTable",
      afterLayout(chart) {
        const area = chart.chartArea;
        if (!area || !containerRef.current || !tableRef.current) return;

        const canvasRect = containerRef.current.getBoundingClientRect();
        const tableRect = tableRef.current.getBoundingClientRect();

        const targetLeft = canvasRect.left + area.left;
        const targetRight = canvasRect.left + area.right;

        const labelWidth = Math.max(110, targetLeft - tableRect.left);
        const totalWidth = labelWidth + (targetRight - targetLeft);

        tableRef.current.style.width = `${totalWidth}px`;
        if (labelColRef.current) {
          labelColRef.current.style.width = `${labelWidth}px`;
        }
      },
    };

    chartInstance.current = new ChartJS(ctx, {
      type: "bar",
      data: {
        labels: chartData.map((d) => d.month),
        datasets: [
          {
            type: "bar",
            label: dataLabels.revenue,
            data: chartData.map((d) => d.revenue),
            backgroundColor: "transparent",
            borderColor: colors.revenue,
            borderWidth: 1.5,
            maxBarThickness: 24,
            categoryPercentage: 1.0,
            barPercentage: 0.9,
            yAxisID: "y",
          },
          {
            type: "line",
            label: dataLabels.parkingBays,
            data: chartData.map((d) => d.parkingBays),
            borderColor: colors.parkingBays + "B3",
            backgroundColor: "transparent",
            borderWidth: 2,
            borderDash: [6, 4],
            pointRadius: 0,
            yAxisID: "y2",
          },
          {
            type: "line",
            label: dataLabels.transaction,
            data: chartData.map((d) => d.transaction),
            borderColor: colors.transaction + "B3",
            backgroundColor: colors.transaction,
            borderWidth: 2,
            pointRadius: 0,
            pointBackgroundColor: colors.transaction,
            yAxisID: "y2",
          },
          {
            type: "line",
            label: dataLabels.occupancy,
            data: chartData.map((d) => d.occupancy),
            borderColor: colors.occupancy + "B3",
            backgroundColor: "transparent",
            borderWidth: 2,
            pointRadius: 0,
            yAxisID: "y1",
          },
        ],
      },
      options: {
        responsive: false,
        maintainAspectRatio: false,
        devicePixelRatio: Math.max(window.devicePixelRatio || 1, 2),
        interaction: { mode: null },
        hover: { mode: null },
        events: [],
        plugins: {
          legend: {
            display: true,
            position: "top",
            labels: {
              color: "#ffffff",
              usePointStyle: false,
              boxWidth: 24,
              boxHeight: 12,
              padding: 6,
              generateLabels: (chart) =>
                chart.data.datasets.map((ds, i) => ({
                  text: ds.label,
                  datasetIndex: i,
                  hidden: false,
                  fillStyle: "transparent",
                  strokeStyle: "transparent",
                  lineWidth: 0,
                  fontColor: "#ffffff",
                })),
            },
          },
          tooltip: { enabled: false },
        },
        scales: {
          x: {
            offset: true,
            grid: { display: false },
            ticks: { display: false },
          },
          y: {
            type: "linear",
            position: "left",
            min: axisRanges.y.min,
            max: axisRanges.y.max,
            ticks: {
              color: themeColors.textColor,
              font: { size: 12 },
              callback: leftFormatter,
            },
            grid: { display: false },
            title: {
              display: true,
              text: yAxisLabel,
              color: themeColors.textColor,
              font: { size: 10, weight: "bold" },
            },
          },
          y1: {
            type: "linear",
            position: "right",
            min: axisRanges.y1.min,
            max: axisRanges.y1.max,
            ticks: {
              color: themeColors.textColor,
              font: { size: 12 },
              stepSize: 15,   
              callback: (v) => (v >= 0 ? rightFormatter(v) : ""),
            },
            grid: { display: false },
            title: {
              display: true,
              text: "Occupancy %",
              color: themeColors.textColor,
              font: { size: 10, weight: "bold" },
            },
          },
          y2: {
            type: "linear",
            position: "left",
            min: axisRanges.y2.min,
            max: axisRanges.y2.max,
            display: false,
            grid: { display: false },
          },
        },
        layout: {
          padding: { left: 24, right: 50, top: 8, bottom: 10 },
        },
      },
      plugins: [
        customLegendPlugin,
        shadowPlugin,
        unifiedLabelPlugin,   // compute value labels
        ramadanPlugin,        // compute ramadan position + nudge clashing labels
        drawValueLabelsPlugin,// draw values (post-nudge)
        drawRamadanBoxesPlugin, // draw ramadan LAST, always on top
        alignTablePlugin,
      ],
    });

    const resizeObserver = new ResizeObserver(() => {
  if (!containerRef.current || !chartInstance.current) return;
  const w = containerRef.current.offsetWidth;
  const h = containerRef.current.offsetHeight;
  chartInstance.current.resize(w, h);
});
if (containerRef.current) resizeObserver.observe(containerRef.current);

return () => {
  resizeObserver.disconnect();
  if (chartInstance.current) chartInstance.current.destroy();
};
  }, [
    chartData,
    axisRanges,
    showDataLabels,
    showRamadanRow,
    getRamadanMonths,
    months,
    yearRanges,
    colors,
    dataLabels,
    leftFormatter,
    rightFormatter,
    ramadanBorderColor,
    yAxisLabel,
  ]);

  // Table rendering
  const groupedData2 = groupDataByYear(chartData);
  const tableMonths = chartData.map((d) => d.month.split("-")[0]);
  const tableYearRanges = Object.keys(groupedData2).map((year) => ({
    year,
    span: groupedData2[year].length,
  }));

  const RevenueIndicator = () => (
    <span style={{ display: "inline-flex", alignItems: "center", marginRight: 4, verticalAlign: "middle" }}>
      <span style={{ display: "inline-block", width: 14, height: 8, backgroundColor: colors.revenue, borderRadius: 1 }} />
    </span>
  );
  const ParkingBaysIndicator = () => (
    <span style={{ display: "inline-flex", alignItems: "center", marginRight: 4, verticalAlign: "middle" }}>
      <svg width="20" height="10" viewBox="0 0 20 10">
        <line x1="0" y1="5" x2="20" y2="5" stroke={colors.parkingBays} strokeWidth="2" strokeDasharray="5,3" />
      </svg>
    </span>
  );
  const TransactionIndicator = () => (
    <span style={{ display: "inline-flex", alignItems: "center", marginRight: 4, verticalAlign: "middle", position: "relative", width: 20, height: 10 }}>
      <span style={{ position: "absolute", top: "50%", left: 0, right: 0, height: 2, backgroundColor: colors.transaction, transform: "translateY(-50%)" }} />
      <span style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 6, height: 6, borderRadius: "50%", backgroundColor: colors.transaction }} />
    </span>
  );
  const OccupancyIndicator = () => (
    <span style={{ display: "inline-flex", alignItems: "center", marginRight: 4, verticalAlign: "middle" }}>
      <span style={{ display: "inline-block", width: 18, height: 2, backgroundColor: colors.occupancy }} />
    </span>
  );

  const safeLeft = (v) => (v == null ? "-" : leftFormatter(v));
  const safeRight = (v) => (v == null ? "-" : rightFormatter(v));
  const safeInt = (v) => (v == null ? "-" : Math.round(v).toLocaleString());

  return (
    <div className="w-full h-full flex items-center justify-center px-4">
      <div
        className="w-full mx-auto px-8 py-8 rounded-lg shadow-lg bg-card"
        style={{ backgroundColor: "#ffffff" }}
      >
        <div
          className="text-center"
          style={{
            backgroundColor: "#ffffff",
            padding: "0.1rem 2rem 0.25rem 2rem",
          }}
        >
          {title && (
            <div style={{ margin: 0, lineHeight: 1.1 }}>
              <HeadingComponent variant={"main"}>{title}</HeadingComponent>
            </div>
          )}
          {subtitle && (
            <div style={{ margin: 0, lineHeight: 1.1 }}>
              <HeadingComponent variant={"sub"}>{subtitle}</HeadingComponent>
            </div>
          )}
        </div>

        <div
          className="w-full p-3"
          style={{ backgroundColor: "#464647", borderRadius: "8px" }}
        >
          <div className="rounded-t-lg pb-0" style={{ paddingLeft: "50px", paddingRight: "2px", paddingTop: "6px" }}>
            {chartTitle && (
              <h3 className="text-center text-base font-semibold mb-0" style={{ color: "#ffffff" }}>
                {chartTitle}
              </h3>
            )}
            <div className="ml-3 h-[350px] relative" ref={containerRef}>

              <canvas ref={chartRef}></canvas>
            </div>
          </div>

        <div className="w-full p-3" style={{ marginLeft: "-20px" }}>
          <div className="overflow-x-auto">
            <table ref={tableRef} className="!border-collapse text-xs table-fixed">
              <colgroup>
                <col ref={labelColRef} style={{ width: "180px" }} />
                {tableMonths.map((_, idx) => (
                  <col key={idx} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th className="py-0.5 px-1 text-center font-normal"></th>
                  {tableMonths.map((month, idx) => (
                    <th key={idx} className="!border !border-border px-0.5 py-0.5 text-center font-normal text-[10px]" style={{ color: "#ffffff", borderColor: "#4b5563" }}>
                      {month}
                    </th>
                  ))}
                </tr>
                <tr>
                  <td className="py-0.5 px-1"></td>
                  {tableYearRanges.map((range, idx) => (
                    <td key={idx} colSpan={range.span} className="!border !border-border px-0.5 py-0.5 text-center text-[10px] font-bold" style={{ color: "#ffffff", borderColor: "#4b5563" }}>
                      {range.year}
                    </td>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { Icon: RevenueIndicator, label: dataLabels.revenue, getData: (d) => safeLeft(d.revenue) },
                  { Icon: ParkingBaysIndicator, label: dataLabels.parkingBays, getData: (d) => safeInt(d.parkingBays) },
                  { Icon: TransactionIndicator, label: dataLabels.transaction, getData: (d) => safeInt(d.transaction) },
                  { Icon: OccupancyIndicator, label: dataLabels.occupancy, getData: (d) => safeRight(d.occupancy) },
                ].map((row, ri) => (
                  <tr key={ri} className="hover:bg-muted/20 transition-colors">
                    <td className="!border !border-border px-1 py-1.5 font-normal text-[6px] text-left whitespace-nowrap" style={{ color: "#ffffff", borderColor: "#4b5563" }}>
                      <row.Icon />
                      {row.label}
                    </td>
                    {chartData.map((item, idx) => (
                      <td key={idx} className="!border !border-border px-0.5 py-1.5 text-center text-[10px]" style={{ color: "#ffffff", borderColor: "#4b5563" }}>
                        {row.getData(item)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-center py-0.5 mt-4 text-[10px] font-bold" style={{ color: "#ffffff" }}>
              {bottomLabel}
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default NewChartKPI;